import type { MediaSource } from '../../lib/mediaReview';
import { readOriginalVideoProperties } from '../../lib/originalVideoProperties';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import { request } from '../../api/client';
import { Modal } from '../../components/Modal';
import { displayBytes, locationQuery, type ExplorerEntry, type FolderLocation } from './explorerApi';
import './file-properties.css';

type Value = string | number | null | undefined;
interface AudioProperties { codec?: string; channels?: number; layout?: string; sampleRate?: number; bitrate?: number }
interface ExrPart { name?: string; width: number; height: number; layers: string[]; channels: Array<{ name: string; type: string }>; compression?: string; chromaticities?: number[] }
interface MediaProperties {
  format?: string; width?: number; height?: number; duration?: number; fps?: number; fpsFraction?: string; variableFps?: boolean | null;
  frameCount?: number; codec?: string; profile?: string; videoBitrate?: number; overallBitrate?: number; overallBitrateEstimated?: boolean;
  pixelFormat?: string; bitDepth?: number; depthLabel?: string; colorSpace?: string; primaries?: string; transfer?: string; range?: string; hdr?: string;
  audio?: AudioProperties[]; alpha?: boolean; colorProfile?: string; mode?: string; pages?: number; dpi?: Array<number | null>; parts?: ExrPart[];
}
export interface FileProperties { name: string; size: number; modified: number; path?: string; kind: 'video' | 'image' | 'file'; extension: string; pending: boolean; message?: string; media?: MediaProperties }
const missing = 'Не указано';
const numeric = (value: number, digits = 3) => value.toLocaleString('ru-RU', { maximumFractionDigits: digits });
const bitrate = (value?: number) => value ? `${numeric(value / 1_000_000)} Мбит/с` : null;
function duration(value?: number) {
  if (value === undefined) return null;
  const seconds = Math.round(value * 1000) / 1000;
  return `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}:${(seconds % 60).toFixed(3).padStart(6, '0')}`;
}
function dimensions(media: MediaProperties) {
  return media.width && media.height ? `${media.width} × ${media.height} пикс.` : null;
}
function ratio(media: MediaProperties) {
  if (!media.width || !media.height) return null;
  const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
  const divisor = gcd(media.width, media.height);
  return `${media.width / divisor}:${media.height / divisor}`;
}
function Rows({ rows }: { rows: Array<[string, Value]> }) {
  return <dl className="sx-file-properties__rows">{rows.map(([label, value]) => <Fragment key={label}><dt>{label}</dt><dd>{value ?? missing}</dd></Fragment>)}</dl>;
}

type PropertiesProps = { onClose: () => void } & ({ file: ExplorerEntry; location: FolderLocation; source?: never } | { source: MediaSource; file?: never; location?: never });
export function FilePropertiesDialog({ file, location, source, onClose }: PropertiesProps) {
  const properties = useQuery({
    queryKey: source ? ['video-properties', source.url, source.videoContext] : ['file-properties', location, file.modified, file.size],
    queryFn: ({ signal }) => source ? readOriginalVideoProperties(source, signal) : request<FileProperties>(`/api/explorer/properties?${locationQuery(location)}`, { signal, cache: 'no-store' }),
    retry: false,
    refetchInterval: query => query.state.data?.pending && query.state.dataUpdateCount < 60 ? 2000 : false,
  });
  const data = properties.data;
  const media = data?.media;
  const dialog = <Modal title="Свойства файла" size="medium" className={`sx-file-properties${source ? ' sx-video-properties' : ''}`} onClose={onClose}>
    <div className="sx-file-properties__body">
      <p className="sx-file-properties__name">{source?.name || file?.name}</p>
      <Rows rows={[
        ['Тип', data?.extension || (source?.name || file?.name || '').split('.').pop()?.toUpperCase()],
        ['Размер файла', data?.size !== undefined || file ? `${displayBytes(data?.size ?? file!.size)} (${(data?.size ?? file!.size).toLocaleString('ru-RU')} байт)` : null],
        ['Изменён', data?.modified !== undefined || file ? new Date((data?.modified ?? file!.modified) * 1000).toLocaleString('ru-RU') : null],
        ['Полный путь', data?.path],
      ]}/>
      {properties.isPending ? <p role="status">Читаем свойства оригинала…</p> : null}
      {properties.error ? <p role="alert" className="sx-error">{properties.error instanceof Error ? properties.error.message : 'Не удалось прочитать свойства файла.'}</p> : null}
      {data?.message ? <p role="status" className="sx-file-properties__note">{data.message}</p> : null}
      {media && data?.kind === 'video' ? <>
        <h3>Видео · оригинал</h3>
        <Rows rows={[
          ['Размер кадра', dimensions(media)], ['Соотношение сторон', ratio(media)],
          ['Частота кадров', media.fps ? `${numeric(media.fps)} fps` : null],
          ['Тип частоты', media.variableFps === true ? 'Переменная (VFR)' : media.variableFps === false ? 'Постоянная (CFR)' : null],
          ['Длительность', duration(media.duration)], ['Количество кадров', media.frameCount?.toLocaleString('ru-RU')],
          ['Видеокодек', [media.codec, media.profile].filter(Boolean).join(' · ') || null],
          ['Битрейт видео', bitrate(media.videoBitrate)],
          ['Общий битрейт', media.overallBitrate ? `${bitrate(media.overallBitrate)}${media.overallBitrateEstimated ? ' (рассчитан средний)' : ''}` : null],
        ]}/>
        <details><summary>Цвет и формат</summary><Rows rows={[
          ['Контейнер', media.format], ['Глубина цвета', media.bitDepth ? `${media.bitDepth} бит на канал` : null],
          ['Формат пикселей', media.pixelFormat], ['Цветовое пространство', media.colorSpace], ['Основные цвета', media.primaries],
          ['Передаточная функция', media.transfer], ['Диапазон', media.range], ['HDR', media.hdr],
        ]}/></details>
        <details><summary>Звук{media.audio?.length ? ` · дорожек: ${media.audio.length}` : ''}</summary>
          {media.audio?.length === 0 ? <p>Звуковых дорожек нет.</p> : media.audio?.map((audio, index) => <section key={index}><h4>Дорожка {index + 1}</h4><Rows rows={[
            ['Кодек', audio.codec], ['Каналы', audio.channels ? `${audio.channels}${audio.layout ? ` · ${audio.layout}` : ''}` : null],
            ['Частота дискретизации', audio.sampleRate ? `${numeric(audio.sampleRate / 1000)} кГц` : null], ['Битрейт', bitrate(audio.bitrate)],
          ]}/></section>)}</details>
      </> : null}
      {media && data?.kind === 'image' ? <>
        <h3>Изображение · оригинал</h3>
        <Rows rows={[
          ['Размер', dimensions(media)], ['Соотношение сторон', ratio(media)], ['Формат', media.format],
          ['Глубина цвета', media.depthLabel || (media.bitDepth ? `${media.bitDepth} бит на канал` : null)],
          ['Прозрачность', media.alpha === true ? 'Есть' : media.alpha === false ? 'Нет' : null],
          ['Цветовой профиль', media.colorProfile],
          ...(media.format === 'TIFF' ? [['Количество страниц', media.pages] as [string, Value]] : []),
        ]}/>
        {media.parts ? <details><summary>Слои, каналы и сжатие EXR</summary>{media.parts.map((part, index) => <section key={index}>
          <h4>{part.name || `Часть ${index + 1}`}</h4><Rows rows={[
            ['Размер', `${part.width} × ${part.height} пикс.`], ['Сжатие', part.compression], ['Слои', part.layers.join(', ')],
            ['Каналы', part.channels.map(c => `${c.name} (${c.type})`).join(', ')],
            ['Хроматические координаты', part.chromaticities?.map(n => numeric(n, 5)).join('; ')],
          ]}/></section>)}</details> : <details><summary>Дополнительно</summary><Rows rows={[
          ['Цветовая модель', media.mode], ['Плотность', media.dpi?.every(n => n != null) ? `${media.dpi.map(n => numeric(n)).join(' × ')} DPI` : null],
        ]}/></details>}
      </> : null}
    </div>
  </Modal>;
  return source ? createPortal(dialog, document.body) : dialog;
}
